import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Search, X, Loader2 } from 'lucide-react';
import { useTraderInstruments } from '@/api/instruments/use-instruments';
import { formatInstrumentPrice, getInstrumentQuotePrecision } from '@/utils/formatters';
import PriceDisplayWithFlash from '@/components/shared/PriceDisplayWithFlash';

interface TradingPairsProps {
    selectedInstrumentId?: string;
    onSelectPair: (instrument: any) => void;
    realTimePrices?: Record<string, { bid: number; ask: number; symbol: string }>;
    isPricesLoading?: boolean;
    pricesError?: string | null;
    marketStatuses?: Record<string, { is_trading_open: boolean; status: string }>;
}

interface InstrumentPair {
    id: string;
    symbol: string;
    name: string;
    assetType: string;
    flag: string;
    precision: number;
    currentPrice: number | null;
    spread: number | null;
}

// Helper function to determine asset type from instrument_type object
const getAssetTypeFromInstrumentType = (instrumentType: any): string => {
    if (!instrumentType) return 'unknown';

    // Handle new detailed format with boolean flags
    if (instrumentType.is_forex) return 'forex';
    if (instrumentType.is_crypto) return 'crypto';
    if (instrumentType.is_stock) return 'stock';
    if (instrumentType.is_commodity) return 'commodity';

    // Fallback to primary_type if available
    if (instrumentType.primary_type && instrumentType.primary_type !== 'unknown') {
        return instrumentType.primary_type;
    }

    // Legacy fallback for string format (backwards compatibility)
    if (typeof instrumentType === 'string' && instrumentType !== 'unknown') {
        return instrumentType;
    }

    return 'unknown';
};

// Helper function to get flag emoji
const getFlagEmoji = (baseAssetSymbol: string): string => {
    const flagMap: Record<string, string> = {
        EUR: '🇪🇺',
        GBP: '🇬🇧',
        JPY: '🇯🇵',
        USD: '🇺🇸',
        CHF: '🇨🇭',
        AUD: '🇦🇺',
        CAD: '🇨🇦',
    };
    return flagMap[baseAssetSymbol] || '🌍';
};

// Helper function to get precision (capped at 5 for compact list display)
const getPrecision = (instrument: any): number =>
    Math.min(getInstrumentQuotePrecision(instrument), 5);

export const TradingPairs = ({
    selectedInstrumentId,
    onSelectPair,
    realTimePrices = {},
    isPricesLoading = false,
    pricesError = null,
    marketStatuses = {},
}: TradingPairsProps) => {
    const { t } = useTranslation('trading');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [instrumentType, setInstrumentType] = useState<string>('all');

    // Simple debounce implementation
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch all instruments with search and type filter (no pagination)
    const {
        data: instrumentsResponse,
        isLoading: isInstrumentsLoading,
        error: instrumentsError,
        isFetching,
    } = useTraderInstruments({
        search: debouncedSearchTerm || undefined,
        type: instrumentType !== 'all' ? instrumentType : undefined,
        per_page: 1000, // Get all instruments in one request
        page: 1,
    });

    const instruments = instrumentsResponse?.data || [];
    const totalInstruments = instruments.length; // Use actual returned count instead of meta.total

    // Create static instrument data that never changes
    const instrumentPairs = useMemo((): InstrumentPair[] => {
        return instruments.map(instrument => {
            // Get price from optimized response (instrument.price object)
            const priceData = (instrument as any).price;
            const currentPrice = priceData?.last ?? priceData?.bid ?? null;
            const spread = instrument.spread ?? null; // spread is still in optimized response

            return {
                id: instrument.id,
                symbol: instrument.symbol,
                name: `${instrument.base_asset?.name || instrument.base_asset?.symbol} / ${instrument.quote_asset?.name || instrument.quote_asset?.symbol}`,
                assetType: getAssetTypeFromInstrumentType(instrument.instrument_type),
                flag: getFlagEmoji(instrument.base_asset?.symbol),
                precision: getPrecision(instrument),
                currentPrice: currentPrice,
                spread: spread,
            };
        });
    }, [instruments]);

    // Helper function to get price data for a pair
    const getPriceData = (pair: InstrumentPair) => {
        const realtimePrice = realTimePrices[pair.id];
        const hasRealtimePrice = realtimePrice?.ask && realtimePrice?.bid;
        const hasInstrumentPrice = pair.currentPrice;

        // Find the instrument object for formatting
        const instrument = instruments.find(inst => inst.id === pair.id);

        // Calculate spread from real-time data or use instrument spread
        const spread = hasRealtimePrice
            ? (realtimePrice.ask - realtimePrice.bid) * 10000 // Convert to pips
            : pair.spread
              ? pair.spread * 10000
              : null;

        if (hasRealtimePrice) {
            // Use real-time bid/ask prices
            return {
                price: formatInstrumentPrice(realtimePrice.ask, instrument),
                priceValue: realtimePrice.ask, // Numeric value for flash effect
                bid: formatInstrumentPrice(realtimePrice.bid, instrument),
                bidValue: realtimePrice.bid, // Numeric value for flash effect
                changePercent: spread,
                hasPriceData: true,
                instrument, // Pass instrument for formatting
            };
        } else if (hasInstrumentPrice) {
            // Calculate fallback bid/ask with proper spread
            const midPrice = pair.currentPrice!;
            const fallbackSpread = pair.spread || 0.0002; // Default 2 pips if no spread available

            // Calculate proper bid/ask
            const halfSpread = fallbackSpread / 2;
            const ask = midPrice + halfSpread;
            const bid = Math.max(0, midPrice - halfSpread); // Ensure bid is never negative

            return {
                price: formatInstrumentPrice(ask, instrument),
                priceValue: ask, // Numeric value for flash effect
                bid: formatInstrumentPrice(bid, instrument),
                bidValue: bid, // Numeric value for flash effect
                changePercent: fallbackSpread * 10000, // Convert to pips for display
                hasPriceData: true,
                instrument, // Pass instrument for formatting
            };
        } else {
            // No price data available
            return {
                price: null,
                priceValue: null,
                bid: null,
                bidValue: null,
                changePercent: null,
                hasPriceData: false,
                instrument: null,
            };
        }
    };

    const getChangeColor = (change: number | null) => {
        if (change === null) return 'text-muted-foreground';
        if (change > 0) return 'text-green-400';
        if (change < 0) return 'text-red-400';
        return 'text-muted-foreground';
    };

    const getRowBgColor = (change: number | null) => {
        if (change === null) return 'hover:bg-accent/50';
        if (change > 0) return 'bg-green-500/10 hover:bg-green-500/20';
        if (change < 0) return 'bg-red-500/10 hover:bg-red-500/20';
        return 'hover:bg-accent/50';
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    return (
        <Card className="h-full bg-card border-border flex flex-col">
            <CardHeader className="pb-2 flex-shrink-0">
                <div className="space-y-2">
                    {/* Search Input and Type Filter */}
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder={t('searchPairs')}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="pl-8 pr-8 h-8 text-xs bg-background border-border text-foreground placeholder:text-muted-foreground"
                            />
                            {searchTerm && (
                                <button
                                    onClick={clearSearch}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                            {isFetching && (
                                <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                </div>
                            )}
                        </div>
                        <Select value={instrumentType} onValueChange={setInstrumentType}>
                            <SelectTrigger className="w-28 h-8 text-xs bg-background border-border">
                                <SelectValue placeholder={t('allTypes')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('allTypes')}</SelectItem>
                                <SelectItem value="forex">{t('forex')}</SelectItem>
                                <SelectItem value="crypto">{t('crypto')}</SelectItem>
                                <SelectItem value="stock">{t('stocks')}</SelectItem>
                                <SelectItem value="commodity">{t('commodities')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Results count and status */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                            {searchTerm
                                ? t('pairsFound', { count: totalInstruments })
                                : t('pairs', { count: totalInstruments })}
                        </span>
                        <div className="flex items-center space-x-2">
                            {isPricesLoading && (
                                <span className="flex items-center">
                                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse mr-1"></div>
                                    {t('updatingPrices')}
                                </span>
                            )}
                            {pricesError && (
                                <span className="text-red-400">{t('priceUpdateFailed')}</span>
                            )}
                        </div>
                    </div>

                    {/* Headers */}
                    <div className="grid grid-cols-[1fr_80px_80px] gap-2 px-2 py-1 items-center">
                        <div className="flex items-center space-x-2">
                            <span className="text-xs w-4"></span> {/* Space for flag */}
                            <span className="text-xs text-muted-foreground font-medium">
                                {t('symbol')}
                            </span>
                        </div>
                        <span className="text-muted-foreground font-medium text-xs text-right">
                            {t('buyHeader')}
                        </span>
                        <span className="text-muted-foreground font-medium text-xs text-right">
                            {t('sellHeader')}
                        </span>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0 flex-1 min-h-0">
                <ScrollArea className="h-full">
                    <div className="space-y-0 pb-4">
                        {isInstrumentsLoading && instrumentPairs.length === 0 ? (
                            <div className="p-4 text-center text-muted-foreground text-sm">
                                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                                {t('loadingInstruments')}
                            </div>
                        ) : instrumentsError ? (
                            <div className="p-4 text-center text-destructive text-sm">
                                {t('failedToLoadInstruments')}
                            </div>
                        ) : instrumentPairs.length === 0 ? (
                            <div className="p-4 text-center text-muted-foreground text-sm">
                                {searchTerm ? t('noPairsFound') : t('noInstrumentsAvailable')}
                            </div>
                        ) : (
                            <>
                                {instrumentPairs.map(pair => {
                                    const priceData = getPriceData(pair);
                                    return (
                                        <div
                                            key={pair.id}
                                            onClick={() => onSelectPair(pair)}
                                            className={`p-2 cursor-pointer transition-colors border-b border-border/50 ${
                                                selectedInstrumentId === pair.id
                                                    ? 'bg-primary/20 border-primary'
                                                    : getRowBgColor(null)
                                            }`}
                                        >
                                            <div className="grid grid-cols-[1fr_80px_80px] gap-2 items-center min-w-0">
                                                <div className="flex items-center space-x-2 min-w-0">
                                                    <span className="text-xs flex-shrink-0">
                                                        {pair.flag}
                                                    </span>
                                                    <div className="flex flex-col min-w-0">
                                                        <div className="flex items-center space-x-1 min-w-0">
                                                            <span className="font-medium text-foreground text-xs truncate">
                                                                {pair.symbol}
                                                            </span>
                                                            {/* Market Status Indicator */}
                                                            {marketStatuses[pair.id] && (
                                                                <div
                                                                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                                                        marketStatuses[pair.id]
                                                                            .is_trading_open
                                                                            ? 'bg-green-500'
                                                                            : 'bg-red-500'
                                                                    }`}
                                                                    title={
                                                                        marketStatuses[pair.id]
                                                                            .status
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                        {pair.name && (
                                                            <span className="text-xs text-muted-foreground truncate">
                                                                {pair.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="text-foreground font-mono text-xs text-right">
                                                    {priceData.hasPriceData &&
                                                    priceData.priceValue !== null &&
                                                    priceData.instrument ? (
                                                        <PriceDisplayWithFlash
                                                            price={priceData.priceValue}
                                                            className="font-mono text-xs"
                                                            formatPrice={p =>
                                                                formatInstrumentPrice(
                                                                    p,
                                                                    priceData.instrument
                                                                )
                                                            }
                                                        />
                                                    ) : (
                                                        <span className="text-red-400">--</span>
                                                    )}
                                                </div>
                                                <div className="text-muted-foreground font-mono text-xs text-right">
                                                    {priceData.hasPriceData &&
                                                    priceData.bidValue !== null &&
                                                    priceData.instrument ? (
                                                        <PriceDisplayWithFlash
                                                            price={priceData.bidValue}
                                                            className="font-mono text-xs"
                                                            formatPrice={p =>
                                                                formatInstrumentPrice(
                                                                    p,
                                                                    priceData.instrument
                                                                )
                                                            }
                                                        />
                                                    ) : (
                                                        <span className="text-red-400">--</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </ScrollArea>
            </CardContent>
        </Card>
    );
};
